import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { ordersService } from '../../services/orders.service';

export interface PodRemark {
  categoryId: number | null;
  quantity: number | null;
  amount: number | null;
  remarks: string;
}

export interface AdditionalCharge {
  chargeTypeId: number | null;
  chargeTypeName: string;
  charge: number | null;
  vendorCharge: number | null;
  chargeToCustomer: boolean;
  chargeToVendor: boolean;
  docUrl: string;
  remarks: string;
  uploading?: boolean;
  saving?: boolean;
  saved?: boolean;
}

export type EndTripMode = 'END_TRIP' | 'ADD_POD' | 'SHOW_POD';

@Component({
  selector: 'app-end-trip',
  templateUrl: './end-trip.component.html',
  styleUrls: ['./end-trip.component.css']
})
export class EndTripComponent implements OnInit {

  orderId: any;
  order: any = null;
  tripData: any = null;
  isLoading = false;

  // ── Mode: drives read-only vs editable rendering ──
  // END_TRIP -> full editable flow (trip still in progress)
  // ADD_POD  -> editable POD/charge forms (trip ended, soft POD received, hard POD pending)
  // SHOW_POD -> fully read-only view (hard POD received) — no forms, no add/save buttons
  mode: EndTripMode = 'END_TRIP';
  selectedHardPodMode: any;

  get isReadOnly(): boolean {
    return this.mode === 'SHOW_POD';
  }

  // ── Section 1: Arrival ──
  arrivalReportingDateTime = '';
  arrivalUnloadingDateTime = '';
  arrivalSaving = false;
  arrivalDateError = '';

  // ── Section 2: POD ──
  savedPods: any[] = [];
  savedPodsLoading = false;

  podCondition: 'CLEAN' | 'REMARK' = 'CLEAN';
  podRemarks: PodRemark[] = [this.emptyRemark()];

  softPodDate = '';
  softPodUrl = '';
  softPodUploading = false;
  podSaving = false;

  hardPodTab: 'PENDING' | 'RECEIVED' | 'LOST' = 'PENDING';
  hardPodMode: number | null = null;
  hardPodSubmissionDate = '';
  hardPodUrl = '';
  hardPodUploading = false;
  hardPodSaving = false;
  indemnityStatus: 'RECEIVED' | 'PENDING' | null = null;
  
  softPodId: number | null = null;
  hardPodId: number | null = null;
  editingSoftPod = false;
  editingHardPod = false;
  trackingId = '';
  courierPartner = '';

  // ── Section 3: Additional Charges ──
  savedCharges: any[] = [];
  savedChargesLoading = false;
  charges: AdditionalCharge[] = [this.emptyCharge()];
  // Single "Save Charges" button (in the common footer) saves every
  // not-yet-saved row in the charges array at once.
  chargesSaving = false;

  // ── Dropdowns ──
  chargeTypeList: any[] = [];
  podCategoryList: any[] = [];
  podModeList: any[] = [];

  closingTrip = false;

  uploadApi = '';

  allCatagory: any;
  quickCodeCategoryList: any;
  sendCatagoryId: any;
  user_details:any;
  roleName:any;
  roleNames: string[] = [];
  showEndTripModal = false;
  endTripRemarks = '';
  minPodDate: string = '';
  lrInfoList:any[]=[]
  lrNumbers:any;
  constructor(
    private activeRoute: ActivatedRoute,
    private router: Router,
    private orderService: ordersService,
    private messageService: MessageService,
    private http: HttpClient
  ) {
    this.allCatagory = JSON.parse(localStorage.getItem('categoryMap')!);
    this.quickCodeCategoryList = JSON.parse(localStorage.getItem('category')!);
    this.sendCatagoryId = this.setId();
    this.getQuickCodeListByCategoryId(this.sendCatagoryId.toString());
    this.user_details = JSON.parse(localStorage.getItem('UserDetails')!);
    // this.roleName = this.user_details.roleName;
    this.roleNames = (this.user_details?.userRoles || this.user_details?.userRoles || []).map((r: any) => r.name);

  }

  ngOnInit(): void {
    this.orderId = this.activeRoute.snapshot.params['id'];

    // Read mode from query params (?mode=SHOW_POD / ADD_POD / END_TRIP)
    const modeParam = this.activeRoute.snapshot.queryParams['mode'] as EndTripMode;
    this.mode = modeParam || 'END_TRIP';

    if (this.orderId) {
      this.isLoading = true;
      this.loadData();
    }
    this.loadDropdowns();
  }

  hasRole(...roles: string[]): boolean {
    return roles.some(r => this.roleNames.includes(r));
  }
  notRole(...roles: string[]): boolean {
    return !this.hasRole(...roles);
  }
  
  // ─────────────────────────────────────────────
  // DATA LOADING
  // ─────────────────────────────────────────────

  loadData(): void {
    this.orderService.getOrderById(this.orderId).subscribe({
      next: (res: any) => {
        this.order = res?.responseObject;

        // If mode wasn't passed explicitly (e.g. deep link / refresh),
        // derive it from the order's own status list so behavior stays
        // consistent with the list screen's End Trip / Add POD / Show POD logic.
        if (!this.activeRoute.snapshot.queryParams['mode']) {
          this.mode = this.deriveModeFromOrder(this.order);
        }

        this.orderService.getOrderTripById(this.orderId).subscribe({
          next: (tripRes: any) => {
            this.isLoading = false;
            this.tripData = tripRes?.responseObject;
            if (this.tripData) {
              this.getLrInfoList(this.tripData?.id)
              this.patchArrivalForm();
              this.hardPodTab = this.tripData?.hardPodStatus || 'PENDING';
            }
            this.loadSavedPods();
            this.loadSavedCharges();
          },
          error: () => { this.isLoading = false; }
        });
      },
      error: () => { this.isLoading = false; }
    });
  }

  onQuantityInput(event: Event,i:any): void {
    const input = event.target as HTMLInputElement;
    const max = Number(this.tripData?.numberOfPackages) || 0;
    const value = Number(input.value);
  
    if (value > max) {
      input.value = max.toString();
      this.podRemarks[i].quantity = max;
    } else {
      this.podRemarks[i].quantity = value;
    }
  }

  deriveModeFromOrder(order: any): EndTripMode {
    const tripEnded = this.checkTripStatus(order, 'TRIP_ENDED');
    const softReceived = this.checkSoftPod(order, 'RECEIVED');
    const hardPending = this.checkHardPod(order, 'PENDING');
    const hardReceived = this.checkHardPod(order, 'RECEIVED');

    if (tripEnded && hardReceived) return 'SHOW_POD';
    if (tripEnded && softReceived && hardPending) return 'ADD_POD';
    return 'END_TRIP';
  }

  checkTripStatus(item: any, status: any): boolean {
    return item?.orderStatus?.some(
      (el: any) => el.statusType === 'TRIP_STATUS' && el.status === status
    ) || false;
  }
  checkHardPod(item: any, status: any): boolean {
    return item?.orderStatus?.some(
      (el: any) => el.statusType === 'HARD_POD_STATUS' && el.status === status
    ) || false;
  }
  checkSoftPod(item: any, status: any): boolean {
    return item?.orderStatus?.some(
      (el: any) => el.statusType === 'SOFT_POD_STATUS' && el.status === status
    ) || false;
  }

  patchArrivalForm(): void {
    this.arrivalReportingDateTime = this.toLocalDatetime(this.tripData?.arrivalReportingDateTime || this.tripData?.reportingDateTime);
    this.arrivalUnloadingDateTime = this.toLocalDatetime(this.tripData?.unloadingDateTime || this.tripData?.loadingDateTime);
    console.log(this.arrivalReportingDateTime,'this.arrivalReportingDateTime')
    console.log(this.arrivalUnloadingDateTime,'this.arrivalUnloadingDateTime')
    // Dates may already span >=1 day when the trip loads (e.g. re-opening
    // an existing draft) — run the same auto-select check here too.
    this.onArrivalDateTimeChange();
  }

  setId(): (number | undefined)[] {
    const keys = [
      'additionalCharges',
      'podCategory',
      'mode'
    ];
    return keys.map((key: any) => this.allCatagory?.[key]?.id);
  }

  getQuickCodeListByCategoryId(ids: any) {
    this.orderService
      .getQuickCodeListByCategoryId(ids)
      .subscribe((data: any) => {
        this.chargeTypeList = data?.responseObject?.additionalCharges;
        this.podCategoryList = data?.responseObject?.podCategory;
        this.podModeList = data?.responseObject?.mode;
        // chargeTypeList may finish loading after the arrival dates were
        // already set — re-run the auto-select now that we have options.
        this.onArrivalDateTimeChange();
      });
  }

  loadDropdowns(): void {
    // this.orderService.getChargeTypes([],0,1000).subscribe({
    //   next: (res: any) => { this.chargeTypeList = res?.responseObject?.content || res?.responseObject || []; },
    //   error: () => {}
    // });
    // this.orderService.getPodCategories([],0,1000).subscribe({
    //   next: (res: any) => { this.podCategoryList = res?.responseObject?.content || res?.responseObject || []; },
    //   error: () => {}
    // });
    // this.orderService.getPodModes([],0,1000).subscribe({
    //   next: (res: any) => { this.podModeList = res?.responseObject?.content || res?.responseObject || []; },
    //   error: () => {}
    // });
  }

  loadSavedPods(): void {
    if (!this.tripData?.id) return;
    const filters = [
      { key: 'TRIP_ID_IN', value: this.tripData.id.toString() },
      { key: 'ORDER_ID_IN', value: (this.order?.id ?? this.orderId)?.toString() }
    ];
    this.savedPodsLoading = true;
    this.orderService.getPods(filters, 0, 10000).subscribe({
      next: (res: any) => {
        this.savedPodsLoading = false;
        this.savedPods = res?.responseObject?.content || res?.responseObject || [];
      },
      error: () => { this.savedPodsLoading = false; }
    });
  }

  loadSavedCharges(): void {
    if (!this.tripData?.id) return;
    const filters = [
      { key: 'TRIP_ID_IN', value: this.tripData.id.toString() },
      { key: 'ORDER_ID_IN', value: (this.order?.id ?? this.orderId)?.toString() }
    ];
    this.savedChargesLoading = true;
    this.orderService.getAdditionalCharges(filters, 0, 20).subscribe({
      next: (res: any) => {
        this.savedChargesLoading = false;
        const list = res?.responseObject?.content || res?.responseObject || [];
        // API returns the field as `chargeType`, not `chargeTypeName` —
        // normalize here so the template can rely on one field name.
        this.savedCharges = list.map((c: any) => ({
          ...c,
          chargeTypeName: c.chargeType || c.chargeTypeName || ''
        }));
      },
      error: () => { this.savedChargesLoading = false; }
    });
  }

  // ─────────────────────────────────────────────
  // DAYS CALCULATION
  // ─────────────────────────────────────────────

  get tripDays(): number {
    if (!this.arrivalReportingDateTime || !this.arrivalUnloadingDateTime) return 0;
    const start = new Date(this.arrivalReportingDateTime).getTime();
    const end   = new Date(this.arrivalUnloadingDateTime).getTime();
    if (isNaN(start) || isNaN(end)) return 0;
    return Math.max(0, Math.floor((end - start) / (1000 * 60 * 60 * 24)));
  }

  get tripDaysLabel(): string {
    const d = this.tripDays;
    if (d === 0) return 'Same day delivery';
    return d === 1 ? '1 day Detention' : `${d} days Detention`;
  }

  /**
   * Fired whenever the reporting or unloading date/time changes.
   * If the resulting trip span is 1 day or more, auto-select "Detention"
   * as the charge Type on the current (last, still-unsaved) charge row —
   * without overriding a type the user has already picked.
   */
  onArrivalDateTimeChange(): void {
    this.arrivalDateError = '';
  
    if (!this.arrivalReportingDateTime || !this.arrivalUnloadingDateTime) return;
  
    if (this.arrivalUnloadingDateTime) {
      this.minPodDate = this.arrivalUnloadingDateTime.split('T')[0];
  
      // Optional: if already selected POD date is before arrival date,
      // reset it to the arrival date.
      if (this.softPodDate && this.softPodDate < this.minPodDate) {
        this.softPodDate = this.minPodDate;
      }
    } else {
      this.minPodDate = '';
    }

    const start = new Date(this.arrivalReportingDateTime).getTime();
    const end = new Date(this.arrivalUnloadingDateTime).getTime();
  
    if (isNaN(start) || isNaN(end)) return;
  
    if (start > end) {
      this.arrivalDateError = 'Reporting date & time cannot be after Unloading date & time.';
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid dates',
        detail: this.arrivalDateError
      });
      return;
    }
  
    if (this.tripDays >= 1) {
      this.autoSelectDetentionCharge();
    }
  }

  private autoSelectDetentionCharge(): void {
    if (!this.chargeTypeList?.length || !this.charges?.length) return;

    const targetCharge = this.charges[this.charges.length - 1];
    if (targetCharge.chargeTypeId) return; // don't clobber a user selection

    const detention = this.chargeTypeList.find((c: any) =>
      (c.name || c.chargeName || '').toLowerCase().includes('detention')
    );
    if (!detention) return;

    targetCharge.chargeTypeId = detention.id;
    targetCharge.chargeTypeName = detention.name || detention.chargeName || '';
  }

  // ─────────────────────────────────────────────
  // SECTION 2: POD
  // ─────────────────────────────────────────────

  emptyRemark(): PodRemark {
    return { categoryId: null, quantity: null, amount: null, remarks: '' };
  }

  addRemark(): void { this.podRemarks.push(this.emptyRemark()); }

  removeRemark(i: number): void {
    if (this.podRemarks.length > 1) this.podRemarks.splice(i, 1);
  }

  onSoftPodFileSelected(event: any): void {
    const file: File = event?.target?.files?.[0];
    if (!file) return;
    this.softPodUploading = true;
    this.uploadFile(file).subscribe({
      next: (url) => { this.softPodUploading = false; this.softPodUrl = url; },
      error: () => {
        this.softPodUploading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Upload failed' });
      }
    });
  }

  savePodDetails(): void {
    if (this.isReadOnly) return;

    if (!this.softPodDate) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'POD date is required' });
      return;
    }

    if (!this.softPodUrl) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Document is required' });
      return;
    }

    if (this.podCondition === 'REMARK') {
      const invalidRemark = this.podRemarks.find(r =>
        !r.categoryId ||
        !r.quantity 
        // ||!r.remarks?.trim()
      );
    
      if (invalidRemark) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Required',
          detail: 'Category, Quantity and Comment are required for every remark.'
        });
        return;
      }
    }

    const lineItems = this.podCondition === 'REMARK'
      ? this.podRemarks
          .filter(r => r.categoryId)
          .map(r => ({
            categoryId: r.categoryId,
            quantity:   r.quantity,
            amount:     r.amount,
            remarks:    r.remarks
          }))
      : [];

    const payload: any = {
      id:         this.softPodId || null,
      orderId:    this.order?.id,
      accountId:  this.order?.accountId || this.tripData?.accountId,
      customerId: this.order?.customerId || this.tripData?.customerId,
      tripId:     this.tripData?.id,
      vendorId:   this.tripData?.vendorId || this.order?.vendorId,
      podType:    'Soft',
      podUrl:     this.softPodUrl || '',
      podDate:    this.toApiDatetime(this.softPodDate),
      isClean:    this.podCondition === 'CLEAN' ? 1 : 0,
      status:     'RECEIVED',
      lineItems
    };

    this.podSaving = true;
    this.orderService.savePod(payload).subscribe({
      next: (res: any) => {
        this.podSaving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'POD details saved successfully' });
        this.loadSavedPods();
      },
      error: (err: any) => {
        this.podSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save POD' });
      }
    });
  }

  onHardPodFileSelected(event: any): void {
    const file: File = event?.target?.files?.[0];
    if (!file) return;
    this.hardPodUploading = true;
    this.uploadFile(file).subscribe({
      next: (url) => { this.hardPodUploading = false; this.hardPodUrl = url; },
      error: () => {
        this.hardPodUploading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Upload failed' });
      }
    });
  }

  get existingSoftPod(): any {
    return this.savedPods.find(p => p.podType === 'Soft');
  }
  get existingHardPod(): any {
    return this.savedPods.find(p => p.podType === 'Hard');
  }
  get showSoftPodForm(): boolean {
    return !this.existingSoftPod || this.editingSoftPod;
  }
  get showHardPodForm(): boolean {
    return !this.existingHardPod || this.editingHardPod;
  }

  editSoftPod(pod: any): void {
    this.softPodId = pod.id;
    this.softPodDate = this.toLocalDateOnly(pod.podDate || pod.createdAt);
    this.softPodUrl = pod.podUrl || '';
    this.podCondition = pod.isClean === 1 ? 'CLEAN' : 'REMARK';
  
    if (this.podCondition === 'REMARK' && pod.lineItems?.length) {
      this.podRemarks = pod.lineItems.map((li: any) => ({
        categoryId: li.categoryId,
        quantity: li.quantity,
        amount: li.amount,
        remarks: li.remarks || ''
      }));
    } else {
      this.podRemarks = [this.emptyRemark()];
    }
  
    this.editingSoftPod = true;
  }
  
  cancelEditSoftPod(): void {
    this.editingSoftPod = false;
    this.softPodId = null;
    this.softPodDate = '';
    this.softPodUrl = '';
    this.podCondition = 'CLEAN';
    this.podRemarks = [this.emptyRemark()];
  }
  
  editHardPod(pod: any): void {
    this.hardPodId = pod.id;
    this.hardPodTab = pod.status || 'RECEIVED';
    this.hardPodMode = pod.modeId || null;
    this.hardPodSubmissionDate = this.toLocalDateOnly(pod.podDate || pod.createdAt);
    this.hardPodUrl = pod.podUrl || '';
    this.trackingId = pod.trackingId || '';
    this.courierPartner = pod.courierPartner || ''
  
    if (this.hardPodTab === 'LOST') {
      this.indemnityStatus = 'RECEIVED';
    }
  
    this.editingHardPod = true;
  }
  
  selectMode(event:any){
    if(event){
      this.podModeList.forEach((el:any)=>{
        if(el.id === event){
          this.selectedHardPodMode = el.name ?? ''
        }
      })
    }
  }

  cancelEditHardPod(): void {
    this.editingHardPod = false;
    this.hardPodId = null;
    this.hardPodMode = null;
    this.hardPodSubmissionDate = '';
    this.hardPodUrl = '';
    this.trackingId = '';
    this.courierPartner = '';
    this.indemnityStatus = null;
  }

  toLocalDateOnly(apiDate: string | undefined): string {
    if (!apiDate) return '';
    const match = apiDate.match(/(\d{2})-(\d{2})-(\d{4})/);
    if (!match) return '';
    const [, dd, mm, yyyy] = match;
    return `${yyyy}-${mm}-${dd}`;
  }

  saveHardPod(): void {
    if (this.isReadOnly) return;

    const payload: any = {
      id:         this.hardPodId || null,
      orderId:    this.order?.id,
      tripId:     this.tripData?.id,
      accountId:  this.order?.accountId || this.tripData?.accountId,
      customerId: this.order?.customerId || this.tripData?.customerId,
      vendorId:   this.tripData?.vendorId || this.order?.vendorId,
      podType:    'Hard',
      status:     this.hardPodTab,
      isClean:    1
    };

    if (this.hardPodTab === 'PENDING') {
      // Just save status as PENDING — no extra fields needed
    }

    if (this.hardPodTab === 'RECEIVED') {
      if (!this.hardPodSubmissionDate) {
        this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Submission date is required' });
        return;
      }
      payload.podUrl  = this.hardPodUrl || '';
      payload.podDate = this.toApiDatetime(this.hardPodSubmissionDate);
      payload.modeId  = this.hardPodMode;
      payload.trackingId = this.trackingId;
      payload.courierPartner = this.courierPartner;
    }

    if (this.hardPodTab === 'LOST') {
      // if (!this.indemnityStatus) {
      //   this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Select indemnity status' });
      //   return;
      // }
      // payload.indemnityStatus = this.indemnityStatus;
      payload.indeminityEnabled = true
    }

    this.hardPodSaving = true;
    this.orderService.savePod(payload).subscribe({
      next: (res: any) => {
        this.hardPodSaving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Hard POD saved successfully' });
        this.loadSavedPods();
      },
      error: (err: any) => {
        this.hardPodSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save hard POD' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // SECTION 3: ADDITIONAL CHARGES
  // ─────────────────────────────────────────────

  emptyCharge(): AdditionalCharge {
    return {
      chargeTypeId: null,
      chargeTypeName: '',
      charge: null,
      vendorCharge: null,
      chargeToCustomer: false,
      chargeToVendor: false,
      docUrl: '',
      remarks: '',
      saved: false
    };
  }

  /**
   * "+ Add More" — purely adds a new blank charge row. It never saves
   * anything; saving only happens from the common "Save Charges" button
   * in the section footer (see saveAllCharges()).
   */
  addAnotherCharge(): void {
    this.charges.push(this.emptyCharge());
  }

  /**
   * Removes a not-yet-saved charge row. Saved rows can't be removed here
   * since they already exist on the backend.
   */
  removeCharge(i: number): void {
    if (this.charges.length > 1) this.charges.splice(i, 1);
  }

  onChargeTypeChange(charge: AdditionalCharge, event: any): void {
    const found = this.chargeTypeList.find(c => c.id === charge.chargeTypeId);
    charge.chargeTypeName = found?.name || found?.chargeName || '';
  }

  onChargeFileSelected(event: any, charge: AdditionalCharge): void {
    const file: File = event?.target?.files?.[0];
    if (!file) return;
    charge.uploading = true;
    this.uploadFile(file).subscribe({
      next: (url) => { charge.uploading = false; charge.docUrl = url; },
      error: () => {
        charge.uploading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Upload failed' });
      }
    });
  }

  private buildChargePayload(charge: AdditionalCharge): any {
    return {
      orderId:          this.order?.id,
      tripId:           this.tripData?.id,
      accountId:        this.order?.accountId || this.tripData?.accountId,
      chargeTypeId:     charge.chargeTypeId,
      chargeTypeName:   charge.chargeTypeName,
      chargeToCustomer: charge.chargeToCustomer,
      chargeToVendor:   charge.chargeToVendor,
      charge:           charge.charge,
      vendorCharge:     charge.chargeToVendor ? (charge.vendorCharge || 0) : undefined,
      docUrl:           charge.docUrl || undefined,
      remarks:          charge.remarks || undefined
    };
  }

  /**
   * Common "Save Charges" action for the whole Additional Charges block.
   * Validates and saves every not-yet-saved row in the `charges` array in
   * one batch — individual rows no longer have their own save button.
   */
  saveAllCharges(): void {
    if (this.isReadOnly) return;

    const toSave = this.charges.filter(c => !c.saved);
    if (!toSave.length) {
      this.messageService.add({ severity: 'info', summary: 'Nothing to save', detail: 'All charges are already saved' });
      return;
    }

    for (const charge of toSave) {
      if (!charge.chargeTypeId || !charge.charge) {
        this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Type and amount are required for every charge' });
        return;
      }
      if (!charge.chargeToCustomer && !charge.chargeToVendor) {
        this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Select at least one charge allocation for every charge' });
        return;
      }
    }

    this.chargesSaving = true;
    toSave.forEach(c => c.saving = true);

    const requests = toSave.map(charge => this.orderService.saveAdditionalCharge(this.buildChargePayload(charge)));

    forkJoin(requests).subscribe({
      next: () => {
        this.chargesSaving = false;
        toSave.forEach(c => { c.saving = false; c.saved = true; });
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Charges saved successfully' });
        this.loadSavedCharges();
      },
      error: (err: any) => {
        this.chargesSaving = false;
        toSave.forEach(c => c.saving = false);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save charges' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // CLOSE TRIP
  // ─────────────────────────────────────────────

  canCloseTrip(): boolean {
    return this.savedPods.length > 0 && !this.arrivalDateError;
  }

  closeTrip(remarks: string): void {
    if (this.isReadOnly) return;
  
    if (this.arrivalDateError) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid dates', detail: this.arrivalDateError });
      return;
    }
    
    if (!this.canCloseTrip()) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'At least one saved POD is required to close the trip' });
      return;
    }
  
    const payload: any = {
      id:                this.tripData?.id,
      orderId:           this.order?.id,
      accountId:         this.order?.accountId || this.tripData?.accountId,
      customerId:        this.order?.customerId || this.tripData?.customerId,
      status:            'TRIP_ENDED',
      reportingDateTime: this.toApiDatetime(this.arrivalReportingDateTime),
      loadingDateTime:   this.toApiDatetime(this.arrivalUnloadingDateTime),
      remarks:           remarks
    };
  
    this.closingTrip = true;
    this.orderService.saveTrip(payload).subscribe({
      next: (res: any) => {
        this.closingTrip = false;
        this.showEndTripModal = false;
        this.messageService.add({ severity: 'success', summary: 'Trip Closed', detail: 'Trip has been ended successfully' });
        this.router.navigate(['/orders']);
      },
      error: (err: any) => {
        this.closingTrip = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to close trip' });
      }
    });
  }

  get isTripEnded(): boolean {
    return this.checkTripStatus(this.order, 'TRIP_ENDED');
  }
  
  openEndTripModal(): void {
    if (!this.canCloseTrip()) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'At least one saved POD is required to close the trip' });
      return;
    }
    this.endTripRemarks = '';
    this.showEndTripModal = true;
  }
  
  cancelEndTripModal(): void {
    this.showEndTripModal = false;
    this.endTripRemarks = '';
  }
  
  confirmCloseTrip(): void {
    if (!this.endTripRemarks?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Please add a comment before ending the trip' });
      return;
    }
    this.closeTrip(this.endTripRemarks.trim());
  }
  // ─────────────────────────────────────────────
  // FILE UPLOAD
  // ─────────────────────────────────────────────

  uploadFile(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    this.uploadApi = this.orderService.uploadFilesURL()
    return this.http.post(this.uploadApi, formData).pipe(
      map((res: any) =>
        res?.responseObject?.url || res?.responseObject?.fileUrl ||
        (typeof res?.responseObject === 'string' ? res.responseObject : '') ||
        res?.url || res?.fileUrl || ''
      )
    );
  }

  // ─────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────

  toLocalDatetime(apiDate: string | undefined): string {
    if (!apiDate) return '';
    const match = apiDate.match(/(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2})/);
    if (!match) return '';
    const [, dd, mm, yyyy, hh, min] = match;
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }

  toApiDatetime(localDt: string): string {
    if (!localDt) return '';
    const [datePart, timePart] = localDt.split('T');
    const [yyyy, mm, dd] = datePart.split('-');
    if (!yyyy || !mm || !dd) return '';
    return `${dd}-${mm}-${yyyy} ${timePart ? `${timePart}:00` : '00:00:00'} +0530`;
  }

  formatApiDate(raw: string | undefined): string {
    if (!raw) return '--';
    const match = raw.match(/(\d{2})-(\d{2})-(\d{4})/);
    if (!match) return raw;
    return `${match[1]}/${match[2]}/${match[3]}`;
  }

  podStatusClass(status: string): string {
    if (status === 'RECEIVED') return 'badge-green';
    if (status === 'LOST') return 'badge-red';
    return 'badge-yellow';
  }

  categoryName(id: number | null | undefined): string {
    if (!id) return '—';
    const c = this.podCategoryList?.find((x: any) => x.id === id);
    return c?.name || c?.categoryName || '—';
  }

  modeName(id: number | null | undefined): string {
    if (!id) return '—';
    const m = this.podModeList?.find((x: any) => x.id === id);
    return m?.name || m?.modeName || '—';
  }

  goBack(): void {
    this.router.navigate(['/orders']);
  }

  saveArrivalDetails(): void {
    if (this.isReadOnly) return;
  
    if (this.arrivalDateError) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid dates', detail: this.arrivalDateError });
      return;
    }
    
    const payload: any = {
      id:                this.tripData?.id,
      orderId:           this.order?.id,
      accountId:         this.order?.accountId || this.tripData?.accountId,
      customerId:        this.order?.customerId || this.tripData?.customerId,
      reportingDateTime: this.toApiDatetime(this.arrivalReportingDateTime),
      loadingDateTime:   this.toApiDatetime(this.arrivalUnloadingDateTime),
    };
  
    this.closingTrip = true;
    this.orderService.saveTrip(payload).subscribe({
      next: (res: any) => {
        this.closingTrip = false;
        this.showEndTripModal = false;
        this.loadData()
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Arrival Details has been ended successfully' });
      },
      error: (err: any) => {
        this.closingTrip = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save arrival details' });
      }
    });
  }

  getLrInfoList(id: any) {
    const filter = [
      { key: 'TRIP_ID_IN', value: id }
    ];
  
    this.orderService.getLrInfoList(filter, 0, 100000).subscribe({
      next: (res: any) => {

        this.lrInfoList = res?.responseObject?.content || [];

        if (this.lrInfoList.length) {

          this.lrNumbers = this.lrInfoList
          .map((item: any) => item?.lrNumber)
          .filter(Boolean)
          .join(', ');

        }
      }
    });
  }

  openInWindow(url: string): void {
    const width = 900;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
  
    window.open(
      url,
      '_blank',
      `width=${width},height=${height},left=${left},top=${top},noopener,noreferrer,resizable=yes,scrollbars=yes`
    );
  }
}